import React, { useState, useEffect } from 'react';
import { Modal } from '../common/Modal';
import { motion } from 'framer-motion';
import { Clock, Check, XCircle, Download, AlertTriangle, Calendar, FileText } from 'lucide-react';
import { useAccommodationStore } from '../../stores/accommodationStore';
import { usePackagesStore } from '../../stores/packagesStore';
import { collection, getDocs, doc, writeBatch, query, where } from 'firebase/firestore';
import { firestore } from '../../lib/firebase/config';
import toast from 'react-hot-toast';
import * as XLSX from 'xlsx';
import { LoadingSpinner } from '../common/LoadingSpinner';

type Props = {
  isOpen: boolean;
  onClose: () => void;
};

type DateConversionEntry = {
  tableId: string;
  tableName: string;
  hotelName: string;
  roomNumber: string;
  bedIndex: number;
  pilgrimId: string;
  pilgrimName: string;
  passportNumber: string;
  oldCheckIn: number;
  oldCheckOut: number;
  newCheckInDate: string;
  newCheckOutDate: string;
  status: 'matched' | 'not_found' | 'error';
  error?: string;
};

export const DateConversionModal = ({ isOpen, onClose }: Props) => {
  const [isScanning, setIsScanning] = useState(false);
  const [isConverting, setIsConverting] = useState(false);
  const [entries, setEntries] = useState<DateConversionEntry[]>([]);
  const [stats, setStats] = useState({
    totalPilgrims: 0,
    matched: 0,
    notFound: 0,
    errors: 0,
    totalTables: 0,
    processedTables: 0
  });
  const [showPreview, setShowPreview] = useState(false);
  
  const { tables } = useAccommodationStore();
  const { packages } = usePackagesStore();

  // Reset state when modal is opened
  useEffect(() => {
    if (isOpen) {
      setIsScanning(false);
      setIsConverting(false);
      setEntries([]);
      setShowPreview(false);
      setStats({
        totalPilgrims: 0,
        matched: 0,
        notFound: 0,
        errors: 0,
        totalTables: 0,
        processedTables: 0
      });
    }
  }, [isOpen]);

  const scanAccommodationTables = async () => {
    setIsScanning(true);
    try {
      const allEntries: DateConversionEntry[] = [];
      const tableValues = Object.values(tables);
      let totalPilgrims = 0;
      let matched = 0;
      let notFound = 0;
      let errors = 0;
      let processedTables = 0;
      
      // 1. Scan all pilgrim data to create a map of pilgrimId -> package name
      console.log('Scanning pilgrims collection...');
      const pilgrimsRef = collection(firestore, 'Pilgrims');
      const pilgrimsSnapshot = await getDocs(pilgrimsRef);
      
      const pilgrimPackages = new Map<string, string>();
      const pilgrimDetails = new Map<string, {name: string, passport: string}>();
      
      pilgrimsSnapshot.forEach(doc => {
        const data = doc.data();
        if (data.package_nickName) {
          pilgrimPackages.set(doc.id, data.package_nickName);
          pilgrimDetails.set(doc.id, {
            name: data.Name || 'Unknown',
            passport: data.Passport_Number || 'Unknown'
          });
        }
      });
      
      console.log(`Found ${pilgrimPackages.size} pilgrims with package information`);
      
      // 2. Process each accommodation table
      for (const table of tableValues) {
        console.log(`Processing table ${table.tn} for hotel ${table.hn}...`);
        processedTables++;
        
        if (!table.r || !Array.isArray(table.r)) {
          console.warn(`Table ${table.tn} has no rooms or rooms is not an array`);
          continue;
        }
        
        // Process each room in the table
        for (const room of table.r) {
          if (!room.b || !Array.isArray(room.b)) continue;
          
          // Process each bed in the room
          for (let bedIndex = 0; bedIndex < room.b.length; bedIndex++) {
            const bed = room.b[bedIndex];
            
            if (!bed.a || !Array.isArray(bed.a)) continue;
            
            // Process each assignment in the bed
            for (const assignment of bed.a) {
              totalPilgrims++;
              
              try {
                const pilgrimId = assignment.pid;
                const packageName = pilgrimPackages.get(pilgrimId);
                const pilgrimDetail = pilgrimDetails.get(pilgrimId);
                
                if (!packageName) {
                  notFound++;
                  allEntries.push({
                    tableId: table.i,
                    tableName: table.tn,
                    hotelName: table.hn,
                    roomNumber: room.n,
                    bedIndex,
                    pilgrimId,
                    pilgrimName: assignment.pn,
                    passportNumber: pilgrimDetail?.passport || 'Unknown',
                    oldCheckIn: assignment.ci,
                    oldCheckOut: assignment.co,
                    newCheckInDate: '',
                    newCheckOutDate: '',
                    status: 'not_found',
                    error: 'Package not found for pilgrim'
                  });
                  continue;
                }
                
                // Find the package
                const pkg = Object.values(packages).find(p => p.name === packageName);
                
                if (!pkg) {
                  errors++;
                  allEntries.push({
                    tableId: table.i,
                    tableName: table.tn,
                    hotelName: table.hn,
                    roomNumber: room.n,
                    bedIndex,
                    pilgrimId,
                    pilgrimName: assignment.pn,
                    passportNumber: pilgrimDetail?.passport || 'Unknown',
                    oldCheckIn: assignment.ci,
                    oldCheckOut: assignment.co,
                    newCheckInDate: '',
                    newCheckOutDate: '',
                    status: 'error',
                    error: `Package "${packageName}" not found in packages collection`
                  });
                  continue;
                }
                
                // Match the hotel name with the package
                let checkInDate = '';
                let checkOutDate = '';
                
                if (pkg.mainMakkahHotel === table.hn) {
                  checkInDate = pkg.mainMakkahStartDate;
                  checkOutDate = pkg.mainMakkahEndDate;
                } else if (pkg.shiftingMakkahHotel === table.hn) {
                  checkInDate = pkg.shiftingMakkahStartDate;
                  checkOutDate = pkg.shiftingMakkahEndDate;
                } else if (pkg.madinahHotel === table.hn) {
                  checkInDate = pkg.madinahStartDate;
                  checkOutDate = pkg.madinahEndDate;
                }
                
                if (!checkInDate || !checkOutDate) {
                  errors++;
                  allEntries.push({
                    tableId: table.i,
                    tableName: table.tn,
                    hotelName: table.hn,
                    roomNumber: room.n,
                    bedIndex,
                    pilgrimId,
                    pilgrimName: assignment.pn,
                    passportNumber: pilgrimDetail?.passport || 'Unknown',
                    oldCheckIn: assignment.ci,
                    oldCheckOut: assignment.co,
                    newCheckInDate: '',
                    newCheckOutDate: '',
                    status: 'error',
                    error: `Hotel "${table.hn}" not found in package "${packageName}"`
                  });
                  continue;
                }
                
                // Found a match
                matched++;
                allEntries.push({
                  tableId: table.i,
                  tableName: table.tn,
                  hotelName: table.hn,
                  roomNumber: room.n,
                  bedIndex,
                  pilgrimId,
                  pilgrimName: assignment.pn,
                  passportNumber: pilgrimDetail?.passport || 'Unknown',
                  oldCheckIn: assignment.ci,
                  oldCheckOut: assignment.co,
                  newCheckInDate: checkInDate,
                  newCheckOutDate: checkOutDate,
                  status: 'matched'
                });
              } catch (error) {
                console.error('Error processing assignment:', error);
                errors++;
              }
            }
          }
        }
      }
      
      // Update state with results
      setEntries(allEntries);
      setStats({
        totalPilgrims,
        matched,
        notFound,
        errors,
        totalTables: tableValues.length,
        processedTables
      });
      
      setShowPreview(true);
      toast.success(`Scan complete: ${matched} dates matched, ${notFound + errors} not matched`);
    } catch (error) {
      console.error('Error scanning accommodation tables:', error);
      toast.error('Error scanning accommodation tables');
    } finally {
      setIsScanning(false);
    }
  };

  const exportPreview = () => {
    try {
      // Create export data
      const exportData = entries.map(entry => ({
        'Hotel Name': entry.hotelName,
        'Table Name': entry.tableName,
        'Room Number': entry.roomNumber,
        'Bed Index': entry.bedIndex + 1,
        'Pilgrim Name': entry.pilgrimName,
        'Passport Number': entry.passportNumber,
        'Old Check In': new Date(entry.oldCheckIn).toLocaleString(),
        'Old Check Out': new Date(entry.oldCheckOut).toLocaleString(),
        'New Check In': entry.newCheckInDate,
        'New Check Out': entry.newCheckOutDate,
        'Status': entry.status === 'matched' ? 'Match Found' : 
                 entry.status === 'not_found' ? 'Not Found' : 'Error',
        'Error': entry.error || ''
      }));
      
      // Create worksheet
      const ws = XLSX.utils.json_to_sheet(exportData);
      
      // Set column widths
      ws['!cols'] = [
        { wch: 20 }, // Hotel Name
        { wch: 20 }, // Table Name
        { wch: 12 }, // Room Number
        { wch: 10 }, // Bed Index
        { wch: 30 }, // Pilgrim Name
        { wch: 15 }, // Passport Number
        { wch: 25 }, // Old Check In
        { wch: 25 }, // Old Check Out
        { wch: 15 }, // New Check In
        { wch: 15 }, // New Check Out
        { wch: 15 }, // Status
        { wch: 30 }, // Error
      ];
      
      // Create workbook and add worksheet
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Date Conversion Preview');
      
      // Generate filename with date
      const date = new Date().toISOString().split('T')[0];
      XLSX.writeFile(wb, `accommodation-date-conversion-preview-${date}.xlsx`);
      
      toast.success('Preview exported successfully');
    } catch (error) {
      console.error('Error exporting preview:', error);
      toast.error('Failed to export preview');
    }
  };

  const executeConversion = async () => {
    if (!window.confirm("Are you sure you want to convert all dates? This action cannot be undone.")) {
      return;
    }
    
    setIsConverting(true);
    try {
      // Group entries by table for batch updates
      const tableEntriesMap = entries.reduce((acc, entry) => {
        if (entry.status !== 'matched') return acc;
        
        if (!acc[entry.tableId]) {
          acc[entry.tableId] = [];
        }
        
        acc[entry.tableId].push(entry);
        return acc;
      }, {} as Record<string, DateConversionEntry[]>);
      
      // Process each table in a separate batch
      for (const [tableId, tableEntries] of Object.entries(tableEntriesMap)) {
        const batch = writeBatch(firestore);
        const tableRef = doc(firestore, 'AccommodationTables', tableId);
        const table = tables[tableId];
        
        if (!table) {
          console.warn(`Table ${tableId} not found in store`);
          continue;
        }
        
        // Deep copy the table data to avoid reference issues
        const updatedTable = JSON.parse(JSON.stringify(table));
        
        // Process each entry
        for (const entry of tableEntries) {
          // Find the room
          const roomIndex = updatedTable.r.findIndex((r: any) => r.n === entry.roomNumber);
          if (roomIndex === -1) continue;
          
          // Get the bed
          const bed = updatedTable.r[roomIndex].b[entry.bedIndex];
          if (!bed || !bed.a) continue;
          
          // Find the assignment
          const assignmentIndex = bed.a.findIndex((a: any) => a.pid === entry.pilgrimId);
          if (assignmentIndex === -1) continue;
          
          // Update the assignment with string dates
          updatedTable.r[roomIndex].b[entry.bedIndex].a[assignmentIndex].ci = entry.newCheckInDate;
          updatedTable.r[roomIndex].b[entry.bedIndex].a[assignmentIndex].co = entry.newCheckOutDate;
        }
        
        // Update the table
        batch.update(tableRef, {
          r: updatedTable.r,
          ua: Date.now()
        });
        
        // Commit the batch
        await batch.commit();
        console.log(`Updated table ${tableId}`);
      }
      
      toast.success('Date conversion completed successfully');
      onClose();
    } catch (error) {
      console.error('Error executing conversion:', error);
      toast.error('Error executing conversion');
    } finally {
      setIsConverting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Convert Dates Format" width="90vw" maxWidth="1200px">
      <div className="space-y-6">
        {/* Instructions */}
        <div className="bg-blue-50 p-4 rounded-lg">
          <div className="flex items-start gap-3">
            <Calendar className="text-blue-500 shrink-0 mt-0.5" size={24} />
            <div>
              <h3 className="font-medium text-blue-800 mb-2">Date Format Conversion</h3>
              <p className="text-blue-700">
                This tool will convert timestamp-based check-in and check-out dates in accommodation tables to string format dates from package data.
                The process will:
              </p>
              <ol className="list-decimal list-inside mt-2 space-y-1 text-blue-700">
                <li>Scan all pilgrims to identify their packages</li>
                <li>Find corresponding dates from packages for each hotel</li>
                <li>Replace timestamp dates with string dates in all accommodation tables</li>
              </ol>
            </div>
          </div>
        </div>

        {/* Scanning Button */}
        {!showPreview && (
          <div className="flex justify-center">
            <button
              onClick={scanAccommodationTables}
              disabled={isScanning}
              className={`px-6 py-3 rounded-lg text-white ${
                isScanning ? 'bg-gray-400' : 'bg-primary-500 hover:bg-primary-600'
              } transition-colors flex items-center gap-3`}
            >
              {isScanning ? (
                <>
                  <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full" />
                  <span>Scanning Accommodation Tables...</span>
                </>
              ) : (
                <>
                  <Clock size={20} />
                  <span>Scan Accommodation Tables</span>
                </>
              )}
            </button>
          </div>
        )}

        {/* Scanning Progress */}
        {isScanning && (
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-medium mb-2">Scanning Progress</h3>
            <div className="w-full bg-gray-200 h-2 rounded-full">
              <div 
                className="bg-primary-500 h-2 rounded-full"
                style={{ width: `${Math.min(100, (stats.processedTables / stats.totalTables) * 100)}%` }}
              ></div>
            </div>
            <div className="flex justify-between text-sm text-gray-500 mt-1">
              <span>{stats.processedTables} of {stats.totalTables} tables</span>
              <span>{Math.min(100, Math.round((stats.processedTables / stats.totalTables) * 100))}%</span>
            </div>
          </div>
        )}

        {/* Results and Preview */}
        {showPreview && (
          <>
            {/* Stats Summary */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="text-sm font-medium text-gray-500 mb-1">Total Pilgrims</h3>
                <p className="text-2xl font-bold">{stats.totalPilgrims}</p>
              </div>
              
              <div className="bg-green-50 p-4 rounded-lg">
                <h3 className="text-sm font-medium text-green-600 mb-1">Matched</h3>
                <p className="text-2xl font-bold text-green-700">{stats.matched}</p>
                <p className="text-sm text-green-600">
                  {stats.totalPilgrims > 0 ? Math.round((stats.matched / stats.totalPilgrims) * 100) : 0}% success rate
                </p>
              </div>
              
              <div className="bg-yellow-50 p-4 rounded-lg">
                <h3 className="text-sm font-medium text-yellow-600 mb-1">Not Found</h3>
                <p className="text-2xl font-bold text-yellow-700">{stats.notFound}</p>
              </div>
              
              <div className="bg-red-50 p-4 rounded-lg">
                <h3 className="text-sm font-medium text-red-600 mb-1">Errors</h3>
                <p className="text-2xl font-bold text-red-700">{stats.errors}</p>
              </div>
            </div>
            
            {/* Preview Table */}
            <div className="bg-white border rounded-lg overflow-hidden">
              <h3 className="px-4 py-3 bg-gray-50 font-medium">Conversion Preview</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Hotel</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Room</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Pilgrim</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Current Check In</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Current Check Out</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">New Check In</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">New Check Out</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {entries.slice(0, 100).map((entry, index) => (
                      <tr key={`${entry.tableId}-${entry.roomNumber}-${entry.bedIndex}-${index}`} 
                          className={entry.status === 'error' ? 'bg-red-50' : 
                                    entry.status === 'not_found' ? 'bg-yellow-50' : ''}>
                        <td className="px-6 py-3 whitespace-nowrap text-sm">{entry.hotelName}</td>
                        <td className="px-6 py-3 whitespace-nowrap text-sm">{entry.roomNumber}</td>
                        <td className="px-6 py-3 whitespace-nowrap">
                          <div className="text-sm">{entry.pilgrimName}</div>
                          <div className="text-xs text-gray-500">{entry.passportNumber}</div>
                        </td>
                        <td className="px-6 py-3 whitespace-nowrap text-sm">
                          {new Date(entry.oldCheckIn).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-3 whitespace-nowrap text-sm">
                          {new Date(entry.oldCheckOut).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-3 whitespace-nowrap text-sm font-medium">
                          {entry.status === 'matched' ? entry.newCheckInDate : '-'}
                        </td>
                        <td className="px-6 py-3 whitespace-nowrap text-sm font-medium">
                          {entry.status === 'matched' ? entry.newCheckOutDate : '-'}
                        </td>
                        <td className="px-6 py-3 whitespace-nowrap">
                          {entry.status === 'matched' && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              <Check size={12} className="mr-1" />
                              Matched
                            </span>
                          )}
                          {entry.status === 'not_found' && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                              <AlertTriangle size={12} className="mr-1" />
                              Not Found
                            </span>
                          )}
                          {entry.status === 'error' && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                              <XCircle size={12} className="mr-1" />
                              Error
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {entries.length > 100 && (
                <div className="px-6 py-3 bg-gray-50 text-center text-sm text-gray-500">
                  Showing 100 of {entries.length} entries. Export to see all entries.
                </div>
              )}
            </div>
            
            {/* Warnings and Notes */}
            <div className="bg-yellow-50 p-4 rounded-lg">
              <div className="flex items-start gap-3">
                <AlertTriangle className="text-yellow-600 mt-0.5 shrink-0" size={20} />
                <div>
                  <h3 className="font-medium text-yellow-800 mb-2">Important Notes</h3>
                  <ul className="list-disc list-inside space-y-1 text-yellow-700">
                    <li>This operation will permanently change the date format in all accommodation tables</li>
                    <li>Unmatched entries will not be modified</li>
                    <li>Export the preview to review all changes before confirming</li>
                    <li>Consider backing up your database before proceeding</li>
                  </ul>
                </div>
              </div>
            </div>
            
            {/* Action Buttons */}
            <div className="flex justify-between">
              <button
                onClick={exportPreview}
                className="px-4 py-2 bg-primary-50 text-primary-600 rounded-lg hover:bg-primary-100 transition-colors flex items-center gap-2"
              >
                <FileText size={20} />
                <span>Export Preview</span>
              </button>
              
              <div className="flex gap-3">
                <button
                  onClick={onClose}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                
                <button
                  onClick={executeConversion}
                  disabled={isConverting || stats.matched === 0}
                  className={`px-6 py-2 rounded-lg text-white transition-colors flex items-center gap-2 ${
                    isConverting || stats.matched === 0
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-primary-500 hover:bg-primary-600'
                  }`}
                >
                  {isConverting ? (
                    <>
                      <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full" />
                      <span>Converting...</span>
                    </>
                  ) : (
                    <>
                      <Check size={20} />
                      <span>Confirm & Convert Dates</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};